import {
  BookOpen,
  Building2,
  Calendar,
  ClipboardList,
  GraduationCap,
  HeartHandshake,
  Home,
  Briefcase,
  FileBarChart,
  LayoutDashboard,
  LayoutGrid,
  MessageCircle,
  Newspaper,
  Settings,
  Shield,
  Tags,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';

export type NavItem = {
  codigoModulo: number;
  label: string;
  descricao: string;
  /** Termos extras da busca do mega menu (ex.: "criança" encontra Alunos). */
  palavrasChave?: string[];
  path: string;
  icon: LucideIcon;
  /** Codigo de acao minima para exibir (qualquer acao do modulo) */
  acoesModulo: number[];
  /** Contador opcional (ex.: nao lidas do chat). */
  badge?: number;
};

type ModuloMeta = {
  label: string;
  descricao: string;
  palavrasChave?: string[];
  path: string;
  icon: LucideIcon;
  acoesModulo: number[];
};

const MODULO_META: Record<number, ModuloMeta> = {
  10: {
    label: 'Escolas',
    descricao: 'Cadastro das unidades da rede',
    palavrasChave: ['unidade', 'filial'],
    path: '/escolas',
    icon: Building2,
    acoesModulo: [101, 102, 103],
  },
  20: {
    label: 'Turmas',
    descricao: 'Turmas e salas de cada escola',
    palavrasChave: ['sala', 'classe'],
    path: '/turmas',
    icon: LayoutGrid,
    acoesModulo: [201, 202, 203],
  },
  30: {
    label: 'Alunos',
    descricao: 'Cadastro e matrícula das crianças',
    palavrasChave: ['criança', 'matrícula'],
    path: '/alunos',
    icon: GraduationCap,
    acoesModulo: [301, 302, 303],
  },
  40: {
    label: 'Responsáveis',
    descricao: 'Familiares e contatos das crianças',
    palavrasChave: ['família', 'pai', 'mãe'],
    path: '/responsaveis',
    icon: HeartHandshake,
    acoesModulo: [401, 402, 403],
  },
  50: {
    label: 'Colaboradores',
    descricao: 'Equipe da escola e seus acessos',
    palavrasChave: ['funcionário', 'professor', 'monitor'],
    path: '/colaboradores',
    icon: Briefcase,
    acoesModulo: [501, 502, 503],
  },
  60: {
    label: 'Cargos',
    descricao: 'Cargos e permissões da equipe',
    palavrasChave: ['permissão', 'função'],
    path: '/cargos',
    icon: Shield,
    acoesModulo: [601, 602, 603],
  },
  70: {
    label: 'Perfis',
    descricao: 'Perfis de acesso reutilizáveis',
    palavrasChave: ['permissão', 'papel'],
    path: '/perfis',
    icon: Shield,
    acoesModulo: [701, 702, 703],
  },
  140: {
    label: 'Pratos',
    descricao: 'Pratos usados na montagem do cardápio',
    palavrasChave: ['comida', 'refeição'],
    path: '/pratos',
    icon: UtensilsCrossed,
    acoesModulo: [1401, 1402, 1403],
  },
  150: {
    label: 'Classificação de pratos',
    descricao: 'Grupos usados para classificar os pratos',
    palavrasChave: ['categoria', 'tipo'],
    path: '/classificacao',
    icon: Tags,
    acoesModulo: [1501, 1502, 1503],
  },
  160: {
    label: 'Cardápio',
    descricao: 'Montagem do cardápio semanal das turmas',
    palavrasChave: ['refeição', 'comida'],
    path: '/cardapio',
    icon: BookOpen,
    acoesModulo: [1601, 1602],
  },
  170: {
    label: 'Agenda',
    descricao: 'Eventos e compromissos da escola',
    palavrasChave: ['calendário', 'evento'],
    path: '/agenda',
    icon: Calendar,
    acoesModulo: [],
  },
  180: {
    label: 'Atividades',
    descricao: 'Publicações do dia: recado, acesso, saúde, soninho, alimentação e higiene',
    palavrasChave: ['feed', 'recado', 'publicação'],
    path: '/publicacoes',
    icon: Newspaper,
    acoesModulo: [],
  },
  230: {
    label: 'Chat',
    descricao: 'Conversas com as famílias',
    palavrasChave: ['mensagem', 'conversa'],
    path: '/chat',
    icon: MessageCircle,
    // Inbox exige VER_CHAT (2301); 2306 so libera iniciar conversa familia.
    acoesModulo: [2301],
  },
  270: {
    label: 'Atas',
    descricao: 'Atas de reunião com gravação e publicação',
    palavrasChave: ['reunião', 'gravação', 'transcrição'],
    path: '/atas',
    icon: ClipboardList,
    acoesModulo: [2701, 2702, 2703, 2704],
  },
  // Cada relatorio novo (2602, 2603...) entra em acoesModulo.
  260: {
    label: 'Relatórios',
    descricao: 'Relatórios gerais da escola',
    palavrasChave: ['relatório'],
    path: '/relatorios',
    icon: FileBarChart,
    acoesModulo: [2601, 2602],
  },
  // Modulo 200 (Layouts de e-mail): acessivel via Configuracoes, nao como item de menu.
};

const PUBLICACAO_MODULOS = [80, 90, 100, 110, 120, 130];

function moduloFromAcao(codigoAcao: number): number {
  return Math.floor(codigoAcao / 100) * 10;
}

export function buildNavItems(acoes: number[]): NavItem[] {
  const items: NavItem[] = [
    {
      codigoModulo: 0,
      label: 'Início',
      descricao: 'Página inicial da escola',
      palavrasChave: ['home', 'painel'],
      path: '/',
      icon: Home,
      acoesModulo: [],
    },
  ];

  const modulos = new Set<number>();
  for (const acao of acoes) {
    modulos.add(moduloFromAcao(acao));
  }

  // Atividades: so VER_* / FAZER_* (mesmo conjunto do GET /v1/publicacoes).
  const temPublicacao = acoes.some((a) => {
    const mod = moduloFromAcao(a);
    if (!PUBLICACAO_MODULOS.includes(mod)) return false;
    const seq = a % 10;
    return seq === 1 || seq === 2;
  });
  if (temPublicacao) {
    modulos.add(180);
  }
  // Agenda: qualquer colaborador autenticado (R7 permissoes / agenda.md)
  modulos.add(170);

  const sorted = [...modulos].sort((a, b) => a - b);
  for (const codigo of sorted) {
    const meta = MODULO_META[codigo];
    if (!meta) continue;
    // acoesModulo vazio = sempre (Agenda/Atividades ja filtrados acima).
    // Com lista: exige pelo menos uma acao listada (sem fallback por modulo —
    // senao 2306 sozinho abriria o Chat sem VER_CHAT).
    const temPermissao =
      meta.acoesModulo.length === 0 || meta.acoesModulo.some((a) => acoes.includes(a));
    if (!temPermissao) continue;
    items.push({
      codigoModulo: codigo,
      label: meta.label,
      descricao: meta.descricao,
      palavrasChave: meta.palavrasChave,
      path: meta.path,
      icon: meta.icon,
      acoesModulo: meta.acoesModulo,
    });
  }

  items.push({
    codigoModulo: 999,
    label: 'Configurações',
    descricao: 'Preferências da escola, layouts de e-mail e auditoria',
    palavrasChave: ['preferências', 'e-mail', 'auditoria'],
    path: '/configuracoes',
    icon: Settings,
    acoesModulo: [],
  });

  return items;
}

export function buildDashboardItem(): NavItem {
  return {
    codigoModulo: 998,
    label: 'Dashboard',
    descricao: 'Indicadores da escola',
    path: '/dashboard',
    icon: LayoutDashboard,
    acoesModulo: [],
  };
}
